import { Video } from '../types/Video';
import { getItemPlaybackDuration } from './videoPlaybackUtils';

/**
 * Represents the timeline position of an item (start and end time in seconds)
 */
export interface TimelineRange {
  start: number;
  end: number;
  item: Video;
}

/**
 * Information about overlays affecting a specific video
 */
export interface OverlayInfo {
  musicTracks: Video[];
  textItems: Video[];
}

/**
 * Calculate timeline positions for all items in the timeline
 *
 * IMPORTANT: Only videos/photos advance the timeline position.
 * Music and text items START at their insertion point but don't push subsequent items forward.
 *
 * Example:
 *   Videos: [0-2s], [2-4s], [4-6s] (6s total)
 *   Music at position "a1" (between video 1 and 2): starts at ~2s, lasts 180s
 *   Text at position "a0" (at start): starts at 0s, lasts 10s
 *   Result: Music overlays videos from 2-6s, text overlays videos from 0-6s
 *
 * @param items - All timeline items sorted by position
 * @returns Array of timeline ranges with start/end times
 */
export function calculateTimelinePositions(items: Video[]): TimelineRange[] {
  const positions: TimelineRange[] = [];

  // First pass: Calculate video/photo timeline positions
  let videoTimelinePosition = 0;
  const videoPositionMap = new Map<string, number>(); // Map item ID to timeline start position
  // Index of each item in `items`, recorded here so the second pass doesn't have
  // to re-scan the array to recover an index this loop already had. Without it
  // the second pass is O(n²) (fixed in #63 — do not reintroduce the findIndex).
  const itemIndexById = new Map<string, number>();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    itemIndexById.set(item.id, i);

    const isPlayableMedia = !item.media_type || item.media_type === 'video' || item.media_type === 'photo';

    if (isPlayableMedia) {
      const duration = getItemPlaybackDuration(item);

      videoPositionMap.set(item.id, videoTimelinePosition);
      positions.push({
        start: videoTimelinePosition,
        end: videoTimelinePosition + duration,
        item
      });

      videoTimelinePosition += duration;
    }
  }

  // Second pass: Place music/text items at their insertion points
  for (const item of items) {
    if (item.media_type === 'music' || item.media_type === 'text') {
      // Where this item sits in the position-sorted array (O(1) via the map above).
      const itemIndex = itemIndexById.get(item.id)!;

      // Find the first video/photo AFTER this item in the sorted array
      let startPosition = 0;
      for (let i = itemIndex + 1; i < items.length; i++) {
        const nextItem = items[i];
        if (videoPositionMap.has(nextItem.id)) {
          startPosition = videoPositionMap.get(nextItem.id)!;
          break;
        }
      }

      // If no video after this item, it starts at the end of the timeline
      if (startPosition === 0 && itemIndex > 0) {
        // Find the last video/photo before this item
        for (let i = itemIndex - 1; i >= 0; i--) {
          const prevItem = items[i];
          if (videoPositionMap.has(prevItem.id)) {
            const prevStart = videoPositionMap.get(prevItem.id)!;
            const prevDuration = getItemPlaybackDuration(prevItem);
            startPosition = prevStart + prevDuration;
            break;
          }
        }
      }

      const duration = getItemPlaybackDuration(item);

      positions.push({
        start: startPosition,
        end: startPosition + duration,
        item
      });

      // Debug logging for text items - useful for debugging timing issues
      // Uncomment to see where text items are being placed in the timeline
      // if (item.media_type === 'text') {
      //   console.log(`[Timeline Calc] Text "${item.custom_text?.substring(0, 20)}..." at index ${itemIndex}:`, {
      //     textSortOrder: item.sortOrder,
      //     calculatedStart: startPosition,
      //     calculatedEnd: startPosition + duration,
      //     duration,
      //   });
      // }
    }
  }

  return positions;
}

/**
 * Get music and text overlays that affect a specific video
 *
 * ⚠️ DO NOT DELETE as "unused by the app". This and `getOverlappingItems()`
 * have no production call sites — `buildOverlayIndicatorMap()` replaced them
 * (#63) — but they are the REFERENCE IMPLEMENTATION the guards diff against:
 * `verify:overlay` (real 648-item project) and `verify:overlay:edge` assert the
 * fast path is byte-identical to these. Deleting them doesn't just remove dead
 * code, it removes the oracle, and the fast path silently stops being checked.
 * That matters because the first version of it dropped music bars from text
 * cards — nothing looked broken, a few bars were just missing.
 *
 * @param allItems - All timeline items sorted by position
 * @param targetIndex - Index of the video to check in allItems array
 * @returns Object containing music tracks and text items affecting this video
 */
export function getOverlaysAffectingVideo(
  allItems: Video[],
  targetIndex: number
): OverlayInfo {
  const positions = calculateTimelinePositions(allItems);
  const targetVideo = allItems[targetIndex];

  if (!targetVideo) {
    return { musicTracks: [], textItems: [] };
  }

  // Find the target video's range by ID (can't use index since positions array has different order)
  const targetRange = positions.find(p => p.item.id === targetVideo.id);

  if (!targetRange) {
    return { musicTracks: [], textItems: [] };
  }

  const musicTracks: Video[] = [];
  const textItems: Video[] = [];

  // Check each item to see if it overlaps with the target video
  positions.forEach((range) => {
    // Check if overlay is active when target starts:
    // 1. Overlay starts BEFORE target, OR
    // 2. Overlay starts at SAME time but has lower position (comes first in sort order)
    // 3. AND overlay is still playing when target starts (ends AFTER target starts)
    const overlaps =
      (range.start < targetRange.start ||
       (range.start === targetRange.start && range.item.sortOrder < targetRange.item.sortOrder)) &&
      range.end > targetRange.start;

    if (overlaps) {
      if (range.item.media_type === 'music') {
        musicTracks.push(range.item);
      } else if (range.item.media_type === 'text') {
        textItems.push(range.item);
      }
    }
  });

  return { musicTracks, textItems };
}

/**
 * Get overlapping items of the same type for a music or text item
 *
 * @param allItems - All timeline items sorted by position
 * @param targetIndex - Index of the music/text item to check
 * @returns Array of overlapping items of the same type
 */
export function getOverlappingItems(
  allItems: Video[],
  targetIndex: number
): Video[] {
  const targetItem = allItems[targetIndex];
  if (!targetItem || (targetItem.media_type !== 'music' && targetItem.media_type !== 'text')) {
    return [];
  }

  const positions = calculateTimelinePositions(allItems);
  const targetRange = positions.find(p => p.item.id === targetItem.id);

  if (!targetRange) {
    return [];
  }

  const overlapping: Video[] = [];

  // Check each item of the same type to see if it overlaps
  positions.forEach((range) => {
    if (range.item.id === targetItem.id) return; // Skip self
    if (range.item.media_type !== targetItem.media_type) return; // Only same type

    // Check if ranges overlap AND the overlapping item comes BEFORE this one in position order
    const overlaps =
      (range.start < targetRange.end && range.end > targetRange.start) &&
      range.item.sortOrder < targetRange.item.sortOrder;
    if (overlaps) {
      overlapping.push(range.item);
    }
  });

  return overlapping;
}

/**
 * Which overlay bars each timeline card should render.
 *
 * `music` / `text` hold exactly the items the card draws a bar for, in the same
 * order the per-card functions produced, so consumers can swap to this without
 * any rendering change.
 */
export interface OverlayIndicators {
  music: Video[];
  text: Video[];
}

/**
 * Build the complete card → overlay-bar map in a single pass (#63).
 *
 * ## Why this exists
 *
 * `VideoTimelineGrid` used to call `getOverlaysAffectingVideo()` once per video
 * AND `getOverlappingItems()` once per text/music item. Each of those re-ran
 * `calculateTimelinePositions()` over the *entire* item list, so the memo whose
 * comment said it existed "to avoid repeated calculations" was in fact causing
 * n of them — O(n²) on every timeline re-render.
 *
 * This computes ranges **once**, then walks the overlays (few) rather than the
 * cards (many), assigning each overlay to the cards it covers.
 *
 * ## Semantics are deliberately identical, not merely similar
 *
 * The two predicates below are copied from `getOverlaysAffectingVideo` and
 * `getOverlappingItems` verbatim, including the `sortOrder` tie-breaks that
 * decide what happens when an overlay and a card start at the same instant.
 * `tests/scripts/bench-overlay-map.ts` asserts byte-identical output against
 * the originals — a faster map that changes which cards show bars is a
 * regression, so keep that check passing if you touch this.
 *
 * Note this returns bars only; bar *length* is intentionally not modelled here
 * (see #63 — the owner settled that bar length stays card-granular).
 *
 * @param items - All timeline items sorted by sort order
 * @returns Map of item id → the music and text items whose bars that card shows
 */
export function buildOverlayIndicatorMap(items: Video[]): Map<string, OverlayIndicators> {
  const positions = calculateTimelinePositions(items);

  const result = new Map<string, OverlayIndicators>();
  for (const item of items) {
    result.set(item.id, { music: [], text: [] });
  }

  const overlayRanges: TimelineRange[] = [];
  for (const range of positions) {
    if (range.item.media_type === 'music' || range.item.media_type === 'text') {
      overlayRanges.push(range);
    }
  }

  // --- Overlays onto cards --------------------------------------------------
  // Original predicate (getOverlaysAffectingVideo), per card/overlay pair:
  //   (overlay.start < card.start
  //     || (overlay.start === card.start && overlay.sortOrder < card.sortOrder))
  //   && overlay.end > card.start
  //
  // So a card is covered iff its start lies in (overlay.start, overlay.end),
  // plus the equal-start case gated on sort order. Both are contiguous runs of
  // cards, so we can seek to the first candidate and walk forward until the
  // overlay ends instead of testing every card.
  //
  // ⚠️ This must run over overlay cards too, not just media cards. A text card
  // still shows the music bar of a track playing underneath it — the grid took
  // that from `overlayInfoMap`, which was built for EVERY item regardless of
  // type. Restricting this to media cards silently drops those bars (caught by
  // the equivalence check in bench-overlay-map.ts).
  // Every card that can host a bar, in ascending start order so the scan below
  // can seek and then stop early rather than testing all n.
  const targetRanges = [...positions].sort((a, b) => a.start - b.start);

  /** First index in targetRanges whose start is >= t. */
  const lowerBound = (t: number): number => {
    let lo = 0;
    let hi = targetRanges.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (targetRanges[mid].start < t) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  for (const overlay of overlayRanges) {
    for (let i = lowerBound(overlay.start); i < targetRanges.length; i++) {
      const target = targetRanges[i];
      if (target.start >= overlay.end) break; // Past this overlay — nothing later can match.
      if (target.item.id === overlay.item.id) continue;

      // Cross-type only: same-type relationships are handled by the pairwise
      // pass below, which uses the different `getOverlappingItems` predicate.
      if (target.item.media_type === overlay.item.media_type) continue;

      const startsBefore =
        overlay.start < target.start ||
        (overlay.start === target.start && overlay.item.sortOrder < target.item.sortOrder);
      if (!startsBefore) continue;

      const bucket = result.get(target.item.id)!;
      if (overlay.item.media_type === 'music') bucket.music.push(overlay.item);
      else bucket.text.push(overlay.item);
    }
  }

  // --- Overlays onto other overlays of the same type ------------------------
  // Original predicate (getOverlappingItems):
  //   ranges intersect AND other.sortOrder < target.sortOrder
  //
  // Overlay counts are small (music/text are a tiny fraction of a project), so
  // the pairwise scan here is bounded by m², not n². Splitting by type first
  // keeps m small in practice.
  const byType = { music: [] as TimelineRange[], text: [] as TimelineRange[] };
  for (const range of overlayRanges) {
    if (range.item.media_type === 'music') byType.music.push(range);
    else byType.text.push(range);
  }

  for (const group of [byType.music, byType.text]) {
    for (const target of group) {
      const bucket = result.get(target.item.id)!;
      const others: Video[] = [];
      for (const other of group) {
        if (other.item.id === target.item.id) continue;
        const overlaps =
          other.start < target.end &&
          other.end > target.start &&
          other.item.sortOrder < target.item.sortOrder;
        if (overlaps) others.push(other.item);
      }

      // Assignment, not merge: for its OWN type a card shows exactly the
      // overlapping set plus itself, and the card's own bar comes last —
      // matching the order the grid appended it in. The cross-type bars written
      // by the pass above live in the other field and are untouched.
      if (target.item.media_type === 'music') {
        bucket.music = [...others, target.item];
      } else {
        bucket.text = [...others, target.item];
      }
    }
  }

  return result;
}

/**
 * Get all active overlays (music and text) at a specific timeline position
 *
 * @param allItems - All timeline items sorted by position
 * @param timelinePosition - Global timeline position in seconds
 * @returns Object containing active music tracks and text items with their offsets
 */
export function getActiveOverlays(
  allItems: Video[],
  timelinePosition: number
): {
  musicTracks: Array<{ item: Video; offset: number; startTime: number; endTime: number }>;
  textItems: Array<{ item: Video; offset: number; startTime: number; endTime: number }>;
} {
  const positions = calculateTimelinePositions(allItems);

  const musicTracks: Array<{ item: Video; offset: number; startTime: number; endTime: number }> = [];
  const textItems: Array<{ item: Video; offset: number; startTime: number; endTime: number }> = [];

  positions.forEach((range) => {
    // Check if current timeline position is within this item's range
    if (timelinePosition >= range.start && timelinePosition < range.end) {
      const offset = timelinePosition - range.start;

      if (range.item.media_type === 'music') {
        musicTracks.push({
          item: range.item,
          offset,
          startTime: range.start,
          endTime: range.end
        });
      } else if (range.item.media_type === 'text') {
        textItems.push({
          item: range.item,
          offset,
          startTime: range.start,
          endTime: range.end
        });
      }
    }
  });

  return { musicTracks, textItems };
}
